import classNames from 'classnames';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import CreateCommunicationHistoryRecordModal from '@/modules/admin-dashboard/components/modals/CreateCommunicationHistoryRecordModal.tsx';
import {
  CommunicationMessage,
  CommunicationThread,
  CommunicationThreadType,
  useCommunicationMessagesQuery,
  useDeleteCommunicationRecordMutation,
} from '@/modules/admin-dashboard/queries/communications.ts';
import {
  Badge,
  BaseModalProps,
  ConfirmModal,
  ModalContentWrapper,
  ModalProviderContext,
  PopoverMenu,
  SelectButtonsNew,
  Spinner,
} from 'ui';
import { AIBeautifyIcon, ClipIcon, FilePlusIcon, TriangleIcon } from 'ui/icons';
import { downloadFile, sanitizeCorrespondenceHtml } from 'ui/utils';

interface CommunicationThreadModalProps extends BaseModalProps {
  thread: CommunicationThread;
}

enum MessageViewMode {
  AIExtracted = 'ai_extracted',
  Original = 'original',
}

const viewModeOptions = [
  {
    value: MessageViewMode.AIExtracted,
    label: (
      <>
        <img src={AIBeautifyIcon} alt="icon" width={12} /> <span> AI Extracted</span>
      </>
    ),
  },
  {
    value: MessageViewMode.Original,
    label: (
      <>
        <img src={FilePlusIcon} alt="icon" width={12} /> <span>Original</span>
      </>
    ),
  },
];

const getCorrespondenceTypeLabel = (
  type?: string,
  threadType: CommunicationThreadType = 'communication' as CommunicationThreadType,
) => {
  if (type === 'physical_mail') return 'Physical mail';
  if (type === 'phone_call') return 'Phone call';
  if (type === 'live_meeting') return 'In-person meeting';
  if (threadType === 'external') return 'Received email';
  return 'Sent email';
};

const formatRecipientPreview = (recipients?: string[]) => {
  if (!recipients?.length) return '---';
  if (recipients.length === 1) return recipients[0];
  return `${recipients[0]} +${recipients.length - 1}`;
};

const renderRecipientList = (recipients?: string[]) => {
  if (!recipients?.length) {
    return '---';
  }

  return (
    <div className="space-y-1">
      {recipients.map((recipient, index) => (
        <div key={`${recipient}-${index}`} className="break-words">
          {recipient}
        </div>
      ))}
    </div>
  );
};

const getThreadTypeLabel = (thread: CommunicationThread) => {
  if (thread.communication_origin === 'platform_notification') return 'Platform notification';
  if (thread.thread_type === 'external') return 'Received email';
  return getCorrespondenceTypeLabel(thread.correspondence_type, thread.thread_type);
};

interface CorrespondenceMessageProps {
  message: CommunicationMessage & { ai_extracted?: string | null };
  threadType: CommunicationThreadType;
}

const CorrespondenceMessage = ({ message, threadType }: CorrespondenceMessageProps) => {
  const [mode, setMode] = useState(
    message.ai_extracted ? MessageViewMode.AIExtracted : MessageViewMode.Original,
  );
  const sanitizedContent = useMemo(
    () => sanitizeCorrespondenceHtml(message.content) || '---',
    [message.content],
  );
  const isPlatformNotification = message.communication_id && !message.sender_name;
  const isHistoricalRecord =
    threadType === 'communication' &&
    message.correspondence_type &&
    message.correspondence_type !== 'email' &&
    !isPlatformNotification;
  const senderDisplayName =
    threadType === 'communication'
      ? isPlatformNotification
        ? 'System'
        : isHistoricalRecord
        ? '-'
        : 'Compli One'
      : message.sender_name || '---';
  const senderDisplayEmail =
    threadType === 'communication' && (isPlatformNotification || isHistoricalRecord)
      ? undefined
      : message.sender_email || 'noreply@complione.com.au';

  const extractInfoItems = useMemo<[string, React.ReactNode][]>(
    () => [
      [
        'from:',
        <>
          {senderDisplayName} {senderDisplayEmail ? `<${senderDisplayEmail}>` : ''}
        </>,
      ],
      ['type:', getCorrespondenceTypeLabel(message.correspondence_type, threadType)],
      ['to:', renderRecipientList(message.to)],
      ['cc:', renderRecipientList(message.cc)],
      [
        'date:',
        message.date || message.created_at
          ? dayjs(message.date || message.created_at).format('ddd, MMM DD, h:mmA')
          : '---',
      ],
      ['subject:', message.subject || '---'],
    ],
    [
      message.cc,
      message.correspondence_type,
      message.created_at,
      message.date,
      message.subject,
      message.to,
      senderDisplayEmail,
      senderDisplayName,
      threadType,
    ],
  );
  return (
    <div className="pb-3">
      <div className="flex justify-between items-center">
        <div className="flex min-w-0 items-center gap-2 flex-wrap">
          <b>{senderDisplayName}</b>
          {senderDisplayEmail && (
            <div className="text-sm text-gray-500 truncate max-w-[300px]">
              {`<${senderDisplayEmail}>`}
            </div>
          )}
        </div>
        <div className="text-xs text-gray-500 shrink-0 self-baseline">
          <div>
            {message.date || message.created_at
              ? dayjs(message.date || message.created_at).format('ddd, MMM DD, h:mmA')
              : '---'}
          </div>
        </div>
      </div>
      <div className="mt-2 flex min-w-0 items-center gap-3">
        <div className="min-w-0 flex-1 text-sm font-medium text-[#111827]">
          <div className="truncate">{message.subject || '---'}</div>
        </div>
      </div>
      <PopoverMenu
        button={
          <div className="flex gap-1 text-sm text-gray-500 mt-1">
            <div className="max-w-52 truncate">to {formatRecipientPreview(message.to)}</div>
            <img alt="details" src={TriangleIcon} />
          </div>
        }
        headerMenu={
          <div className="relative z-20 p-4 rounded shadow space-y-1 text-sm">
            {extractInfoItems.map(([label, value], index) => (
              <div className="flex gap-4" key={index}>
                <div className="w-16 text-gray-500 text-end shrink-0">{label}</div>
                <div>{value}</div>
              </div>
            ))}
          </div>
        }
        items={[]}
        classes={{ panel: 'z-10' }}
      />
      <div
        className={classNames('mt-4', {
          'whitespace-break-spaces': mode === MessageViewMode.AIExtracted,
        })}
      >
        {mode === MessageViewMode.AIExtracted &&
          (message.ai_extracted?.replaceAll('\n\n', '\n') || '---')}
        {mode === MessageViewMode.Original && (
          <div
            className="max-w-[512px] overflow-auto [&_a]:text-[#2563EB] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-[#1D4ED8] [&_a:focus-visible]:outline [&_a:focus-visible]:outline-2 [&_a:focus-visible]:outline-offset-2 [&_a:focus-visible]:outline-[#2563EB]"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        )}
      </div>
      {!!message.ai_extracted && (
        <SelectButtonsNew
          classes={{
            container: 'mt-2 w-fit',
            button: 'flex gap-1 justify-center !min-h-5 !h-5 !w-[108px] text-xs',
            buttonsContainer: '!min-h-6 !h-6',
          }}
          options={viewModeOptions}
          value={mode}
          onChange={setMode}
        />
      )}
      {!!message.attachments?.length && (
        <div className="flex relative py-2 mt-4 gap-2 border-b flex-wrap">
          {message.attachments.map(attachment => (
            <Badge
              key={attachment.object_key}
              className="flex gap-1 w-fit !text-gray-500 max-w-[90%] cursor-pointer hover:!text-[#0369A1]"
              onClick={() => downloadFile(attachment.url, attachment.file_name)}
            >
              <img src={ClipIcon} alt="file" />
              <span className="break-words overflow-hidden" style={{ wordBreak: 'break-word' }}>
                {attachment.file_name}
              </span>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export const CommunicationThreadModal = ({
  open,
  onClose,
  thread,
}: CommunicationThreadModalProps) => {
  const { openModal, onCloseAll } = useContext(ModalProviderContext);
  const deleteRecordMutation = useDeleteCommunicationRecordMutation();
  const messagesQuery = useCommunicationMessagesQuery(thread.thread_id, thread.thread_type);
  const modalScrollRef = useRef<HTMLDivElement>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const activeMessage = useMemo(() => {
    const messages = messagesQuery.data?.data || [];
    return messages[messages.length - 1];
  }, [messagesQuery.data?.data]);

  useEffect(() => {
    if (modalScrollRef.current) {
      modalScrollRef.current.scrollTop = modalScrollRef.current.scrollHeight;
    }
  }, [messagesQuery.data?.data]);

  const handleEditMessage = (message: CommunicationMessage) => {
    onClose();
    openModal(CreateCommunicationHistoryRecordModal, {
      record: message,
    });
  };

  const handleDeleteMessage = (message: CommunicationMessage) => {
    onClose();
    openModal(ConfirmModal, {
      title: 'Delete communication record',
      content: 'Are you sure you want to delete this communication record?',
      closeAfterConfirm: false,
      onConfirm: async () => {
        if (pendingDeleteId === message.id) return;
        onCloseAll();
        setPendingDeleteId(message.id);
        try {
          await deleteRecordMutation.mutateAsync({
            correspondence_id: message.id,
            optimistic_thread_id: thread.thread_id,
            optimistic_thread_type: thread.thread_type,
          });
          const remainingMessages = messagesQuery.data?.data || [];
          if (remainingMessages.length <= 1) {
            return;
          }
        } finally {
          setPendingDeleteId(current => (current === message.id ? null : current));
        }
      },
    });
  };

  const handleEditActiveMessage = () => {
    if (!activeMessage) return;
    handleEditMessage(activeMessage);
  };

  const handleDeleteActiveMessage = () => {
    if (!activeMessage) return;
    handleDeleteMessage(activeMessage);
  };

  return (
    <ModalContentWrapper
      open={open}
      onClose={onClose}
      title={thread.subject || 'Communication thread'}
      classes={{ dialogPanel: 'max-w-[560px]', children: 'max-w-[inherit]' }}
      variant="right"
      modalScrollRef={modalScrollRef}
      onEdit={activeMessage ? handleEditActiveMessage : undefined}
      onDelete={
        activeMessage && pendingDeleteId !== activeMessage.id
          ? handleDeleteActiveMessage
          : undefined
      }
    >
      <div className="mb-4 text-sm text-[#6B7280]">
        Type: <span className="text-[#111827]">{getThreadTypeLabel(thread)}</span>
      </div>
      {messagesQuery.isLoading && (
        <div className="h-72 flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      {!messagesQuery.isLoading && (
        <div className="space-y-4">
          {(messagesQuery.data?.data || []).map(message => (
            <CorrespondenceMessage
              key={message.id}
              message={message as CommunicationMessage & { ai_extracted?: string | null }}
              threadType={thread.thread_type}
            />
          ))}
        </div>
      )}
    </ModalContentWrapper>
  );
};

export default CommunicationThreadModal;
